// Port of TrailNavigation/StageLayout.swift.

import type { StageKeyPoint, TrailStage } from '../core/models';
import { polylineLength } from '../core/geo';
import type { RouteTrack } from './routeTrack';

/** Where a stage lies on a route, in route (travel-direction) distances. */
export interface StageSpan {
  stageID: string;
  startDistance: number;
  endDistance: number;
  /** The stage record, when it was supplied. */
  stage?: TrailStage;
  /** True when the stage is travelled against its canonical direction. */
  isReversed: boolean;
}

/** A stage key point positioned on the route. */
export interface RouteKeyPoint {
  /** The key point with `distanceFromStart` expressed in travel direction within its stage. */
  keyPoint: StageKeyPoint;
  /** Distance from the route start. */
  routeDistance: number;
  stageID: string;
}

function sequentialStarts(lengths: readonly number[]): number[] {
  const starts: number[] = [];
  let running = 0;
  for (const length of lengths) {
    starts.push(running);
    running += Number.isFinite(length) ? Math.max(0, length) : 0;
  }
  return starts;
}

/** Maps stages and their key points onto a `RouteTrack`. */
export const StageLayout = {
  /**
   * Stage spans in travel order.
   *
   * Uses `route.stageIDs` / `route.stageStartDistances` when present (the normal case: the route was built from
   * stages); start distances are re-derived from the stage polylines if the two arrays disagree. A route without
   * stage metadata (bare imported track) lays the supplied stages out sequentially in the given order.
   */
  spans(track: RouteTrack, stages: readonly TrailStage[]): StageSpan[] {
    const route = track.route;
    const total = track.totalDistance;
    const byID = new Map<string, TrailStage>();
    for (const stage of stages) if (!byID.has(stage.id)) byID.set(stage.id, stage);

    let ids: readonly string[];
    let starts: readonly number[];
    let reversed: boolean;
    if (route.stageIDs.length > 0) {
      ids = route.stageIDs;
      starts = route.stageStartDistances;
      reversed = route.isReversed;
      if (starts.length !== ids.length) {
        starts = sequentialStarts(ids.map((id) => {
          const stage = byID.get(id);
          return stage ? polylineLength(stage.polyline) : 0;
        }));
      }
    } else if (stages.length > 0) {
      ids = stages.map((s) => s.id);
      starts = sequentialStarts(stages.map((s) => polylineLength(s.polyline)));
      reversed = false;
    } else {
      return [];
    }

    const spans: StageSpan[] = [];
    let previousStart = 0;
    ids.forEach((id, index) => {
      const rawStart = Number.isFinite(starts[index]) ? starts[index] : previousStart;
      const start = Math.min(Math.max(rawStart, previousStart), total);
      const rawEnd = index + 1 < starts.length && Number.isFinite(starts[index + 1]) ? starts[index + 1] : total;
      const end = Math.min(Math.max(rawEnd, start), total);
      spans.push({ stageID: id, startDistance: start, endDistance: end, stage: byID.get(id), isReversed: reversed });
      previousStart = start;
    });
    return spans;
  },

  /**
   * Key points of all spans positioned on the route, sorted by route distance.
   *
   * `distanceFromStart` is expressed in the stage's declared frame (`stage.distance`); it is scaled to the stage's
   * length on the route geometry and mirrored for reversed travel (`length - offset`), so a pass at 4.5 km of a 6 km
   * stage sits 1.5 km into the reversed stage.
   */
  keyPoints(spans: readonly StageSpan[]): RouteKeyPoint[] {
    const result: RouteKeyPoint[] = [];
    for (const span of spans) {
      const stage = span.stage;
      if (!stage) continue;
      const length = span.endDistance - span.startDistance;
      const frame = Number.isFinite(stage.distance) && stage.distance > 0 ? stage.distance : length;
      for (const keyPoint of stage.keyPoints) {
        if (!Number.isFinite(keyPoint.distanceFromStart)) continue;
        const offset = Math.min(Math.max(keyPoint.distanceFromStart, 0), frame);
        const scaled = frame > 0 ? (offset / frame) * length : 0;
        const travelOffset = span.isReversed ? length - scaled : scaled;
        result.push({
          keyPoint: { ...keyPoint, distanceFromStart: travelOffset },
          routeDistance: span.startDistance + travelOffset,
          stageID: span.stageID,
        });
      }
    }
    result.sort((a, b) => {
      if (a.routeDistance !== b.routeDistance) return a.routeDistance - b.routeDistance;
      return a.keyPoint.id < b.keyPoint.id ? -1 : a.keyPoint.id > b.keyPoint.id ? 1 : 0;
    });
    return result;
  },

  /** Index of the span containing `distance` (last span whose start ≤ distance), or `undefined` if none. */
  spanIndex(spans: readonly StageSpan[], distance: number): number | undefined {
    if (spans.length === 0) return undefined;
    let lo = 0;
    let hi = spans.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (spans[mid].startDistance <= distance) lo = mid + 1;
      else hi = mid;
    }
    return Math.max(0, lo - 1);
  },
};
